import React, { useState } from 'react';
import styled from 'styled-components';
import { FaGithub, FaExternalLinkAlt } from 'react-icons/fa';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
  padding: 0 20px;
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 0 10px;
    gap: 16px;
  }
`;

const Card = styled.a`
  display: flex;
  flex-direction: column;
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.1);
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);

  &:hover {
    transform: translateY(-6px);
    border-color: rgba(34, 211, 238, 0.3);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3), 0 0 20px rgba(34, 211, 238, 0.08);
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  ${Card}:hover & img {
    transform: scale(1.05);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(transparent, rgba(30, 41, 59, 0.9));
  }
`;

const CardBody = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const CardTitle = styled.h3`
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary, #e2e8f0);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  svg {
    color: var(--text-muted, #64748b);
    transition: color 0.2s ease;
    flex-shrink: 0;
    margin-left: 8px;
  }

  ${Card}:hover & svg {
    color: var(--accent-cyan, #22d3ee);
  }
`;

const CardDescription = styled.p`
  font-family: var(--font-sans, 'Inter', sans-serif);
  font-size: 0.82rem;
  color: var(--text-secondary, #94a3b8);
  line-height: 1.6;
  margin: 0 0 16px 0;
  flex: 1;
`;

const TechTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Tag = styled.span`
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 0.65rem;
  color: var(--accent-cyan, #22d3ee);
  background: rgba(34, 211, 238, 0.08);
  border: 1px solid rgba(34, 211, 238, 0.15);
  padding: 3px 10px;
  border-radius: 20px;
  transition: all 0.2s ease;

  ${Card}:hover & {
    border-color: rgba(34, 211, 238, 0.3);
    background: rgba(34, 211, 238, 0.12);
  }
`;

const ShowMoreBtn = styled.button`
  display: block;
  margin: 30px auto 0;
  padding: 10px 28px;
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 0.85rem;
  color: var(--accent-cyan, #22d3ee);
  background: transparent;
  border: 1px solid rgba(34, 211, 238, 0.3);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(34, 211, 238, 0.08);
    border-color: rgba(34, 211, 238, 0.5);
    transform: translateY(-2px);
  }
`;

const ProjectGrid = ({ data }) => {
  const [showAll, setShowAll] = useState(false);
  const visibleProjects = showAll ? data : data.slice(0, 6);

  const getLinkIcon = (url) => {
    if (url.includes('github.com')) return <FaGithub size={16} />;
    return <FaExternalLinkAlt size={14} />;
  };

  return (
    <div>
      <Grid>
        {visibleProjects.map((project, index) => (
          <Card
            key={index}
            href={project.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <ImageWrapper>
              <img src={project.image} alt={project.caption} loading="lazy" />
            </ImageWrapper>
            <CardBody>
              <CardTitle>
                <span>{project.caption}</span>
                {getLinkIcon(project.url)}
              </CardTitle>
              <CardDescription>{project.description}</CardDescription>
              <TechTags>
                {project.tech && project.tech.map((t, i) => (
                  <Tag key={i}>{t}</Tag>
                ))}
              </TechTags>
            </CardBody>
          </Card>
        ))}
      </Grid>
      {data.length > 6 && !showAll && (
        <ShowMoreBtn onClick={() => setShowAll(true)}>
          View All Projects ({data.length})
        </ShowMoreBtn>
      )}
      {showAll && data.length > 6 && (
        <ShowMoreBtn onClick={() => setShowAll(false)}>
          Show Less
        </ShowMoreBtn>
      )}
    </div>
  );
};

export default ProjectGrid;
